import { useState } from 'react';
import { isEmpty } from 'lodash';
import { useRouter } from 'next/router';
import { Post } from 'lib/types';
import { Credit } from 'lib/types/credits';
import { Tag } from 'lib/types/tags';
import { userIsProOrAdminOrOG } from 'lib/helpers';
import { RolesProvider } from 'lib/contexts/rolesContext';
import { useUpgradeModal } from 'components/UpgradeModal';
import { useApi, useCurrentUser } from 'lib/contexts/ApplicationState';
import {
  Add, Info, Tag as TagIcon, FilterNone, FolderOpen,
} from '@mui/icons-material';
import { MobileTooltip } from 'components/MobileTooltip';
import { UsernameModal } from 'components/UsernameModal';
import { useModal } from 'mui-modal-provider';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { BulkSavePostsToProjectsModal } from 'components/SaveToList/BulkSavePostsToProjectsModal';
import { DrawerType, Method } from './AddDrawer';
import { FilterPostResult } from './FilterPostResult';

interface Props {
  posts: Post[];
  isLoading: boolean;
  error?: Error;
  selectedPostIds: number[];
  setSelectedPostIds: (ids: number[]) => void;
  drawer?: DrawerType;
  method?: Method;
  setDrawer: (drawer: DrawerType | undefined) => void;
  setSimilarPosts: (posts: Post[]) => void;
  setToCopyTags: (tags: Tag[]) => void;
  setToCopyCredits: (credits: Credit[]) => void;
}

export const Manager = ({
  posts, isLoading, error, selectedPostIds, setSelectedPostIds, drawer, method,
  setDrawer, setSimilarPosts, setToCopyTags, setToCopyCredits,
}: Props) => {
  const router = useRouter();
  const currentUser = useCurrentUser();
  const { showModal } = useModal();
  const API = useApi();

  const [showDialog, setShowDialog] = useState(false);
  const [usernameInfo, setUsernameInfo] = useState<string | undefined>();

  const [selectedMatchId, setSelectedMatchId] = useState<string | undefined>();

  const { showUpgradeModal } = useUpgradeModal();

  const disabled = !currentUser || !selectedPostIds || isEmpty(selectedPostIds);

  const showSimilarPosts = async () => {
    setSimilarPosts([]);
    const postCount = selectedPostIds.length;
    if (postCount > 0) {
      const postId = selectedPostIds[0];
      const p = await API.getSimilarPosts(postId);
      setSimilarPosts(p.filter((_p) => !selectedPostIds.find((m) => m === _p.id)));
    }
  };

  const openOrUpgrade = (feature: string, open: () => void) => {
    if (!currentUser?.username) {
      setShowDialog(true);
      setUsernameInfo(`Please add a username to ${feature}.`);
      return;
    }

    if (userIsProOrAdminOrOG(currentUser)) {
      open();
    } else {
      showUpgradeModal(`Upgrade to ${feature}`);
    }
  };

  const giveCredits = () => {
    setDrawer('credit');
  };

  const copy = (post: Post) => {
    openOrUpgrade('copy from a post', () => {
      setToCopyCredits(post.credits || []);
      setToCopyTags(post.tags || []);
      window.scrollTo({ top: 0 });
    });
  };

  const selectMatch = async (matchId: string) => {
    const alreadySelected = selectedMatchId === matchId;
    if (!alreadySelected && selectedMatchId) {
      // if a match is already selected, ask if user wants to merge
      const modal = showModal(ConfirmationModal, {
        text: 'You have selected two existing separate matches.  Would you like to merge them?  This action cannot be undone.',
        primaryAction: 'Y',
        onYes: async () => {
          await API.mergeMatches(selectedMatchId, [matchId]);
          modal.destroy();
        },
        onNo: () => {
          modal.destroy();
        },
      });
      return;
    }

    if (!alreadySelected) {
      setSelectedMatchId(matchId);
    } else {
      setSelectedMatchId(undefined);
    }
  };

  const handleSaveToProject = () => {
    const modal = showModal(BulkSavePostsToProjectsModal, {
      postIds: selectedPostIds,
      onClose: () => {
        modal.destroy();
      },
    });
  };

  return (
    <RolesProvider>
      <div className="flex flex-wrap sm:flex-nowrap justify-between items-center">
        <div className="flex items-center mr-2 my-2">
          <div className="relative mr-2">
            {disabled && (
              <MobileTooltip
                arrow
                className="absolute -top-2 -right-2"
                title="To add credits, first select a post."
              >
                <Info />
              </MobileTooltip>
            )}
            <button
              type="button"
              disabled={disabled}
              onClick={() => openOrUpgrade('bulk assign credits', giveCredits)}
              className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
            >
              <Add fontSize="small" className="mr-1" />
              Add Credits
            </button>
          </div>
          <div className="relative mr-2">
            {disabled && (
              <MobileTooltip
                arrow
                className="absolute -top-2 -right-2"
                title="To add tags, first select a post."
              >
                <Info />
              </MobileTooltip>
            )}

            <button
              type="button"
              disabled={disabled}
              className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
              onClick={() => openOrUpgrade('bulk add tags', () => setDrawer('tag'))}
            >
              <TagIcon fontSize="small" className="mr-1" />
              Add Tags
            </button>
          </div>
          <div className="relative mr-2">
            {disabled && (
              <MobileTooltip
                arrow
                className="absolute -top-2 -right-2"
                title="To match posts, first select a post."
              >
                <Info />
              </MobileTooltip>
            )}

            <button
              type="button"
              disabled={disabled}
              className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
              onClick={() => {
                setDrawer('match');
                showSimilarPosts();
              }}
            >
              <FilterNone fontSize="small" className="mr-1" />
              Match Posts
            </button>
          </div>
          <div className="relative mr-2">
            {disabled && (
              <MobileTooltip
                arrow
                className="absolute -top-2 -right-2"
                title="To add to projects, first select a post."
              >
                <Info />
              </MobileTooltip>
            )}

            <button
              type="button"
              onClick={handleSaveToProject}
              className="btn-primary-xs md:whitespace-nowrap uppercase flex items-center"
              disabled={disabled}
            >
              <FolderOpen fontSize="small" className="mr-1" />
              Add to project
            </button>
          </div>
        </div>
      </div>
      <div className="mt-2 w-full">
        <FilterPostResult
          posts={posts}
          selectedPostIds={selectedPostIds}
          isLoading={isLoading}
          error={error}
          onChange={setSelectedPostIds}
          drawerOpen={drawer !== undefined}
          method={method}
          onCopy={copy}
          onSelectMatch={selectMatch}
        />
      </div>
      {showDialog
        && <UsernameModal saveLabel="SAVE" info={usernameInfo} onCancel={() => setShowDialog(false)} onSave={() => router.reload()} />}
    </RolesProvider>
  );
};
